import { IGatsbyImageData } from 'gatsby-plugin-image';
import { RestaurantStatus } from '../restaurant/restaurant.types';
import { IIntegrationsResponse } from '../integrations/integrations.types';

export enum COMPONENT_KEY {
  HEADER = 'HEADER',
  HERO = 'HERO',
  ABOUT_US = 'ABOUT_US',
  LOCATION = 'LOCATION',
  FOOTER = 'FOOTER',
  IMAGE_GALLERY = 'IMAGE_GALLERY',
  FEATURED_MENU = 'FEATURED_MENU',
  DEALS = 'DEALS',
  SPOTLIGHTS = 'SPOTLIGHTS',
  CUSTOM_SECTION = 'CUSTOM_SECTION',
  MENU = 'MENU',
  CALL_TO_ACTION = 'CALL_TO_ACTION',
  HOW_IT_WORKS = 'HOW_IT_WORKS',
  GIFT_CARDS = 'GIFT_CARDS',
  TIERS_POINTS = 'TIERS_POINTS',
  LOYALTY_FORM = 'LOYALTY_FORM',
  FORM = 'FORM',
  CATERING_FORM = 'CATERING_FORM',
  INFO_CARDS = 'INFO_CARDS',
  TESTIMONIALS = 'TESTIMONIALS',
  ACCESSIBILITY = 'ACCESSIBILITY',
  PACKAGES = 'PACKAGES',
}

enum VARIANT_KEY {
  VARIANT1 = 'VARIANT1',
  VARIANT2 = 'VARIANT2',
  VARIANT3 = 'VARIANT3',
  VARIANT4 = 'VARIANT4',
}

enum ButtonVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

interface IColors {
  primary: string | null;
  secondary: string | null;
  tertiary: string | null;
}

export interface IComponentBuilderConfig {
  builderMode: boolean;
  previewMode?: boolean;
  activePage?: string;
  screenSize?: SCREEN_SIZE;
  homePageId?: string;
  logoConfig: ILogoConfig;
  favicon?: IFavicon;
  navLinks: ILink[];
  pageUrls?: IPageUrl;
  isBuilderDesktopMode?: boolean;
  isComponentActive?: boolean;
  isAccessibility?: boolean;
  brandbook: any;
  socials: any;
  onBoarding: any;
  builderConfigData: any;
  colors: IColors;
  isPublishedDesktopMode?: boolean;
  slug?: string;
  componentOrderNumber: number;
  stickyButtonToggle?: boolean;
  shouldUsePrimaryHeading?: boolean;
  seoTitle?: string;
}

enum ButtonCornerVariant {
  SHARP_CORNERS = 0,
  SOFT_CORNERS = 14,
  ROUNDED = 49,
}

export type StyleProperties = {
  [key: string]: string | number | { [key: string]: string };
};

export type ButtonCornerStyles = {
  [ButtonCornerVariant.SHARP_CORNERS]: number;
  [ButtonCornerVariant.SOFT_CORNERS]: number;
  [ButtonCornerVariant.ROUNDED]: number;
};

export interface ButtonVariantStyles {
  [ButtonVariant.PRIMARY]: StyleProperties;
  [ButtonVariant.SECONDARY]: StyleProperties;
  [ButtonVariant.TERTIARY]: StyleProperties;
}

interface NestedStyle {
  [key: string]: string | number | NestedStyle;
}

enum HEADER_STATE {
  STICKY = 'sticky',
  SCROLL_UP = 'scrollUp',
  DEFAULT = 'default',
}

export type MediaMap = Map<
  string,
  {
    url: string;
    type: string;
    localFile: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
        original: {
          width: number;
          height: number;
          src: string;
        };
      } | null;
      fields: null | {
        publicURL: string;
      };
    };
  }
>;

export interface INavlink {
  id: number;
  link: string;
  label: string;
  internal: boolean;
  pageId: string;
  sectionId?: string;
}

export interface BuilderConfig {
  stickyButtonToggle?: boolean;
  logoConfig: {
    header: {
      primary: {
        image: string | undefined;
        link: string;
        id: number;
        altText: string;
        name: string;
        [key: string]: any;
      };
      secondary: {
        image: string | undefined;
        link: string;
        id: number;
        altText: string;
        name: string;
        [key: string]: any;
      };
    };
    footer: {
      primary: {
        image: string | undefined;
        link: string;
        id: number;
        altText: string;
        name: string;
        [key: string]: any;
      };
    };
  };
  navLinks: ILink[];
  pageUrls: { [key: string]: string };
  isBuilderDesktopMode?: boolean;
  isPublishedDesktopMode?: boolean;
  builderMode?: boolean;
  previewMode?: boolean;
  componentOrderNumber: number;
  shouldUsePrimaryHeading?: boolean;
  seoTitle?: string;
  activePage?: string;
  onBoarding: {
    address: string;
    phoneNo: string;
    longitude: number;
    latitude: number;
    email: string;
    name?: string;
    schedule: {
      toggle: boolean;
      days: null | {
        [key: string]: {
          toggle: boolean;
          open: string;
          close: string;
          status: RestaurantStatus;
        };
      };
    };
  };
  socials: {
    toggle: boolean;
    icons: {
      [key: string]: {
        toggle: boolean;
        link: string;
      };
    };
  };
  builderConfigData: {
    restaurantId: string;
    businessId: string;
    environmentUrl: string;
    businessAlias: string;
    restaurantAlias: string;
    onlineOrderingUrl: string;
    openTableId?: string;
    websiteId: string;
    pageName: string;
    siteUrl?: string;
    name?: string;
  };
  isAccessibility?: boolean;
  slug: string;
  colors: {
    primary: string | null;
    secondary: string | null;
    tertiary: string | null;
  };
}

export interface IActiveComponent {
  id: string;
  type: COMPONENT_KEY;
  variant: VARIANT_KEY;
}

export type IActiveSection = keyof typeof VARIANT_KEY;

export interface ILink {
  id: string;
  link: string;
  label: string;
  internal: boolean;
  sectionId?: string | null;
  pageId?: string | null;
  children: ILink[];
  noLink?: boolean;
  level: 1 | 2 | 3;
}

export interface IFavicon {
  id: number | null;
  fileStoreId: number | null;
  name: string;
  size: number | null;
  type: string;
  url: string;
  fileFor: string;
}

export interface IPageUrl {
  [key: string]: string;
}

interface Image {
  croppedFileUrl?: string;
  fileUrl: string;
  altText: string;
  imageName: string;
}

interface InlineTextSelectionStyles {
  [key: string]: string | number | undefined;
}

export interface InlineTextSelectionConfig {
  selectedText: string;
  selectionRange: {
    start: number;
    end: number;
  };
  currentStyles?: InlineTextSelectionStyles;
  dataKey: string;
}

interface CustomizationSectionProps {
  imageCustomizationProps: {
    setImageAltTextFunction?: (text: string) => void;
    setCroppedImageFunction?: (fileUrl: string) => void;
    imageData?: Image;
    setLoading?: (loading: boolean) => void;
  };
  inlineTextSelection?: InlineTextSelectionConfig | null;
  activeFieldSection?: string | null;
  expandedSection?: string | null;
}

export enum SCREEN_SIZE {
  DESKTOP = 'DESKTOP',
  MOBILE = 'MOBILE',
}
export enum PUBLISH_ENV {
  STAGING = 'STAGING',
  PRODUCTION = 'PRODUCTION',
}
export interface IRootBuilderConfig {
  logoConfig: ILogoConfig;
  favicon: IFavicon;
  navLinks: ILink[];
  pageUrls: IPageUrl;
  activePage: string;
  builderBasicConfig: {
    builderMode: boolean;
    previewMode: boolean;
    screenSize: SCREEN_SIZE;
    isBuilderDesktopMode?: boolean;
    isPublishedDesktopMode?: boolean;
    builderFullyInitialized?: boolean;
    otherPanelConfig: CustomizationSectionProps;
    isPublishing: boolean;
    lastPublishEnv?: PUBLISH_ENV | null;
  };
  syncBuilderConfig: {
    isSyncing?: boolean;
    syncFunction?: () => void;
    isNewChanges?: boolean;
    lastSyncedAt?: string;
  };
  builderHistoryConfig: {
    undoFunction?: () => void;
    redoFunction?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
  };
}

export enum SyncBrandbookLevelEnums {
  LEVELONE = 'One',
  LEVELTWO = 'Two',
  LEVELTHREE = 'Three',
  DONE = 'Done',
}

export enum WebsiteStatus {
  DRAFT = 'draft',
  PUBLISHED_LIVE = 'published_live',
  PUBLISHED_TEMPORARY = 'published_temporary',
}

export interface DomainSettings {
  newDomainSetup?: boolean;
  domainName?: string | null;
  nsServers?: string[] | null;
  cloudFrontDomainName?: string | null;
  isDomainVerified?: boolean;
  isCertificateValid?: boolean | null;
  cloudFrontDomainNameStaging?: string | null;
  lastPublishEnv?: PUBLISH_ENV | string | null;
  id?: number;
  restaurantId?: number;
  createdAt?: Date;
  updatedAt?: Date;
  hostedZoneId?: string | null;
  certificateArn?: string | null;
  s3BucketName?: string | null;
  distributionId?: string | null;
  deletedAt?: Date | null;
  lastPublishDate?: Date | null;
  lastPublishDateStaging?: Date | null;
  publishedTemplateId?: number;
  publishedProductionWebsiteId?: string | null;
  publishedTemporaryWebsiteId?: string | null;
  lastAdaScan?: any;
}

export interface WebsiteRecord {
  id: string;
  restaurantId: number;
  templateId: number;
  draftName?: string | null;
  status: WebsiteStatus;
  lastPublishStatus?: GithubJobStatusEnum | null;
  lastPublishConclusion?: GithubJobConclusionEnum | null;
  lastPublishDate?: Date | null;
  lastPublishDateStaging?: Date | null;
  lastWorkflowRunId?: string | null;
  isSiteVerifiedOnGoogle?: boolean;
  isSitemapSubmitted?: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
  websiteData?: any;
}

interface SocialLinksType {
  id: number;
  name: string;
  value: string;
  icon?: React.ReactNode;
}

interface ColorItem {
  id: number;
  hex: string;
  name: string;
  isDefault?: boolean;
}

interface ImagesType {
  id?: number;
  fileStoreId?: number;
  thumbnailUrl?: string;
  compressedFileUrl?: string;
  fileUrl: string;
  name?: string;
  size?: number;
  type?: string;
  fileStoreCategoryId?: number;
  fileFor?: string;
}

export type IPublishableData = Pick<
  IBuilderData,
  | 'globalStyles'
  | 'favicon'
  | 'homePageId'
  | 'HeaderComponent'
  | 'FooterComponent'
  | 'logoConfig'
  | 'navLinks'
  | 'brandbook'
  | 'socials'
  | 'onBoarding'
  | 'pages'
  | 'builderConfigData'
  | 'pageUrls'
  | 'stickyButtonToggle'
>;

export interface IBuilderData extends IRootBuilderConfig {
  globalStyles: GlobalStyles;
  activeComponent: IActiveComponent;
  activeConfigPanel: CONFIG_PANEL_KEYS;
  stickyButtonToggle: boolean;
  pages: {
    [key: string]: IPageData;
  };
  brandbook: {
    logos: ImagesType[] | [];
    contentImages: ImagesType[] | [];
    colors: ColorItem[] | [];
    socialMedia: SocialLinksType[] | [];
    fonts: FontsType | null;
    updatedAt: Date | null;
  };
  socials: {
    toggle: boolean;
    icons: {
      [key: string]: {
        toggle: boolean;
        link: string;
      };
    };
  };

  builderConfigData: {
    brandbookSync: SyncBrandbookLevelEnums;
    otherColors: any[];
    restaurantId: number;
    businessId: number;
    environmentUrl: string;
    onlineOrderingUrl: string;
    openTableId?: string;
    businessAlias: string;
    restaurantAlias: string;
    siteUrl: string;
    pageIdCounter?: number;
    templateId?: number;
    componentIdCounter?: number;
    websiteId: string | null;
    initializedComponents?: {
      [key: string]: boolean;
    };
    trackingIds?: TrackingIds;
    loadingIntegrations?: boolean;
  };
  onBoarding: {
    address: string;
    phoneNo: string;
    latitude: number;
    longitude: number;
    email: string;
    schedule: {
      toggle: boolean;
      days: null | {
        [key: string]: {
          toggle: boolean;
          status: RestaurantStatus;
          open: string;
          close: string;
        };
      };
    };
  };
  homePageId: string;
  HeaderComponent: IWidget;
  FooterComponent: IWidget;
  domainSettings: DomainSettings;
  integrations?: IIntegrationsResponse;
}

export interface ILogoConfig {
  header: ILogoType;
  footer: ILogoType;
}

export interface ILogoType {
  primary?: {
    [key: string]: string | number | any;
  };
  secondary?: {
    [key: string]: string | number | any;
  };
}

interface Meta {
  title: string;
  description: string;
  image: string;
  url: string;
}

export enum SOCIAL_PLATFORMS {
  FACEBOOK = 'facebook',
}
export interface SocialMeta {
  [SOCIAL_PLATFORMS.FACEBOOK]: Meta;
}

export interface TrackingIds {
  facebookPixelId?: string | null;
  tiktokPixelId?: string | null;
  gtagId?: string | null;
}

export interface SEOMeta {
  title: string;
  description: string;
}
export interface SiteSeoFormProps {
  formData: SEOMeta;
  updateForm: (key: SEO_META_KEYS, value: string) => void;
  url: string;
}
export enum SEO_META_KEYS {
  TITLE = 'title',
  DESCRIPTION = 'description',
}

export interface IPageData {
  key: string;
  meta: {
    label: string;
    slug?: string;
    seo: SEOMeta;
    social: SocialMeta;
  };
  components: {
    [key: string]: IWidget;
  };
  componentOrder: string[];
  MainComponent: IWidget;
  initializedComponents?: {
    [key: string]: boolean;
  };
}

export interface IWidget {
  id: string;
  type: COMPONENT_TYPE;
  component: COMPONENT_KEY;
  variant: string;
  name: string;
  disabled?: boolean;
  isInitialized?: boolean;
  props: {
    styles: Record<string, any>;
    config: Record<string, any>;
    // Add media-specific props for mobile typography
    'media-s'?: {
      styles: Record<string, any>;
      config: Record<string, any>;
    };
    setComponentData?: (key: string, value: string) => void;
  };
}

export enum COMPONENT_TYPE {
  SECTION = 'section',
  ELEMENT = 'element',
}

export interface IPage {
  label: string;
  slug: string;
  key: PAGE_KEY;
}

export enum CONFIG_PANEL_KEYS {
  BASIC_CONFIG = 'BASIC_CONFIG',
  GLOBAL_STYLES = 'GLOBAL_STYLES',
  NEW_PAGE = 'NEW_PAGE',
  NEW_SECTION = 'NEW_SECTION',
  EDIT_SECTION = 'EDIT_SECTION',
  IMAGE_CUSTOMIZATION = 'IMAGE_CUSTOMIZATION',
  EDIT_SPOTLIGHT = 'EDIT_SPOTLIGHT',
  INLINE_TEXT_STYLES = 'INLINE_TEXT_STYLES',
}

export enum PAGE_KEY {
  HOME_PAGE = 'HOME_PAGE',
  ABOUT_US = 'ABOUT_US',
  MENU = 'MENU',
  CONTACT_US = 'CONTACT_US',
  GALLERY = 'GALLERY',
  CATERING = 'CATERING',
  RESERVATIONS = 'RESERVATIONS',
  LOYALTY = 'LOYALTY',
  LOYALTY_FORM = 'LOYALTY_FORM',
  GIFT_CARDS = 'GIFT_CARDS',
  ACCESSIBILITY = 'ACCESSIBILITY',
}

export interface Style {
  fontSize: number;
  fontWeight: number;
  fontFamily: string;
  fontStyle: string;
  lineHeight: number;
  letterSpacing: number;
  [key: string]: number | string;
}

export interface FontsType {
  [key: string]: Style;
}

export enum RadiusEnum {
  SHARP = 'sharp',
  SOFT = 'soft',
  CUSTOM = 'custom',
}

export enum MapTheme {
  LIGHT = 'light',
  DARK = 'dark',
}
export interface RadiusType {
  radius: RadiusEnum;
  value: number;
}

export type AnimationMode = 'standard' | 'cascade';

export interface GlobalStyles {
  button: NestedStyle;
  heading: NestedStyle;
  subHeading: NestedStyle;
  bodyText: NestedStyle;
  scrollIcon?: string;
  backgroundColor: string;
  buttonVariants: ButtonVariantStyles;
  headerState: HEADER_STATE;
  headerVariant?: IActiveSection;
  buttonRadius?: number;
  imageRadius?: RadiusType;
  cardRadius?: RadiusType;
  animation?: {
    enabled: boolean;
    type: 'fadeInUp' | 'fadeInDown' | 'fadeInLeft' | 'fadeInRight';
    mode?: AnimationMode;
  };
}

export interface IButtonConfig {
  toggle: boolean;
  internal: boolean;
  link: string;
  label: string;
  pageId: string;
  sectionId: string;
  variant: {
    type: 'radio';
    value: ButtonVariant;
  };
  fullWidth?: boolean;
  // cornerVariant?: {
  //   type: 'radio';
  //   value: ButtonCornerVariant;
  // };
}

/**
 * Partial type for button configuration updates
 * Used across multiple components for handling button config changes
 */
export type PartialButtonConfig = Partial<IButtonConfig>;

export enum GithubJobStatusEnum {
  QUEUED = 'queued',
  COMPLETED = 'completed',
  IN_PROGRESS = 'in_progress',
  NOT_FOUND = 'not_found',
}

export enum GithubJobConclusionEnum {
  SKIPPED = 'skipped',
  CANCELLED = 'cancelled',
  SUCCESS = 'success',
  FAILED = 'failure',
}
